import { c as _c } from "react/compiler-runtime";
import { Icon, type IconNames, Image, Price } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useEffect, useRef } from 'react';
import Highlighter from 'react-highlight-words';
import { decodeEntityString, getOptionsFromPrice, getPrices, getPriceVariant, isPriceRange } from '@libs/algolia';
import { useDecisions } from '@libs/common-next';
import type { ListItemProps, PanelItemProps } from '../types';
type AlgoliaHighlightQuery = {
  matchedWords: string[];
};
type AlgoliaHighlightMatchLevel = {
  matchLevel: string;
};
function ListItem(t0) {
  const $ = _c(17);
  const {
    sourceId,
    item
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      recentSearchesPlugin: "clock-outline",
      querySuggestionsPlugin: "search"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const iconNames = t1;
  const itemName = item.name ?? item.label ?? item.objectID;
  const matchLevel = (item?._highlightResult?.query as AlgoliaHighlightMatchLevel)?.matchLevel ?? "none";
  const itemNameAsString = decodeURIComponent(encodeURI(itemName as string));
  let t2;
  if ($[1] !== sourceId) {
    t2 = iconNames[sourceId] && <Icon className="autocomplete-list-item__icon" name={iconNames[sourceId]} />;
    $[1] = sourceId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item._highlightResult?.query || $[4] !== itemName || $[5] !== itemNameAsString || $[6] !== matchLevel) {
    t3 = itemName && <span className="autocomplete-list-item__name" data-testid="item-name"><Highlighter highlightClassName="normal-text" unhighlightClassName={matchLevel !== "none" ? "highlighted-text" : "normal-text"} searchWords={(item?._highlightResult?.query as AlgoliaHighlightQuery)?.matchedWords ?? []} textToHighlight={itemNameAsString} /></span>;
    $[3] = item._highlightResult?.query;
    $[4] = itemName;
    $[5] = itemNameAsString;
    $[6] = matchLevel;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== item.__autocomplete_qsCategory) {
    t4 = item.__autocomplete_qsCategory as string && !(item.__autocomplete_qsCategory as string).includes("+") && <span className="autocomplete-list-item__category">— in {item.__autocomplete_qsCategory as string}</span>;
    $[8] = item.__autocomplete_qsCategory;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="autocomplete-list-item__main">{t2}{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon className="autocomplete-list-item__icon__arrow autocomplete-assist-search" name="navigation-arrow" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5) {
    t7 = <div className="autocomplete-list-item__button-content" data-testid="list-item-suggestion">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function ProductItem(t0) {
  const $ = _c(28);
  const {
    item
  } = t0;
  const dropdownPriceDecision = useDecisions("x_drpdwn_price");
  const imageUrl = item.images?.small ?? item.images?.large;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== dropdownPriceDecision || $[1] !== imageUrl || $[2] !== item.name || $[3] !== item.priceData) {
    const isRange = isPriceRange(item.priceData);
    const shouldUseOptionsFrom = dropdownPriceDecision === "options_from" && isRange;
    const optionsFromPrice = shouldUseOptionsFrom ? getOptionsFromPrice(item.priceData) : undefined;
    let t7;
    if ($[10] !== item.priceData) {
      t7 = getPriceVariant(item.priceData);
      $[10] = item.priceData;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let priceVariant = t7;
    if (optionsFromPrice) {
      priceVariant = optionsFromPrice.priceVariant === "disabled" ? "disabled" : "primary";
    }
    t4 = "autocomplete-list-item__product";
    t5 = "product-item-suggestion";
    if ($[12] !== imageUrl || $[13] !== item.name) {
      t6 = imageUrl && <Image src={imageUrl} alt={`${item.name} image`} loading="lazy" width={64} height={64} />;
      $[12] = imageUrl;
      $[13] = item.name;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t1 = cn("autocomplete-list-item__main", {
      "autocomplete-list-item__main--options-from": shouldUseOptionsFrom
    });
    let t8;
    if ($[15] !== item.name) {
      t8 = decodeEntityString(item.name);
      $[15] = item.name;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    if ($[17] !== t8) {
      t2 = <div className="autocomplete-list-item__product-name">{t8}</div>;
      $[17] = t8;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    t3 = dropdownPriceDecision !== "no_price" && <Price className={cn("autocomplete-list-item__price", {
      "autocomplete-list-item__price--options-from": shouldUseOptionsFrom
    })} info={shouldUseOptionsFrom ? "Options from" : undefined} isRange={shouldUseOptionsFrom ? false : isRange} values={optionsFromPrice?.prices ?? getPrices(item.priceData, isRange)} variant={priceVariant} />;
    $[0] = dropdownPriceDecision;
    $[1] = imageUrl;
    $[2] = item.name;
    $[3] = item.priceData;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <div className={t4} data-testid={t5}>{t6}{t7}</div>;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
export function PanelItem(t0) {
  const $ = _c(24);
  let className;
  let focus;
  let item;
  let onClick;
  let other;
  let sourceId;
  let t1;
  if ($[0] !== t0) {
    const {
      className: t2,
      onKeyDown,
      focus: t3,
      item: t4,
      sourceId: t5,
      onClick: t6,
      ...t7
    } = t0;
    className = t2;
    focus = t3;
    item = t4;
    sourceId = t5;
    onClick = t6;
    other = t7;
    t1 = e => {
      onKeyDown(e);
      if (e.key === "Enter") {
        e.currentTarget.click();
      }
    };
    $[0] = t0;
    $[1] = className;
    $[2] = focus;
    $[3] = item;
    $[4] = onClick;
    $[5] = other;
    $[6] = sourceId;
    $[7] = t1;
  } else {
    className = $[1];
    focus = $[2];
    item = $[3];
    onClick = $[4];
    other = $[5];
    sourceId = $[6];
    t1 = $[7];
  }
  const handleKeyDown = t1;
  const buttonRef = useRef(null);
  let t2;
  let t3;
  if ($[8] !== focus) {
    t2 = () => {
      if (focus) {
        buttonRef.current?.focus();
      } else {
        buttonRef.current?.blur();
      }
    };
    t3 = [focus];
    $[8] = focus;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== className) {
    t4 = cn("autocomplete-list-item", className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const liClassName = t4;
  let t5;
  if ($[13] !== item || $[14] !== sourceId) {
    t5 = sourceId !== "productSuggestionsPlugin" ? <ListItem sourceId={sourceId} item={item} /> : <ProductItem item={item} />;
    $[13] = item;
    $[14] = sourceId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== handleKeyDown || $[17] !== onClick || $[18] !== t5) {
    t6 = <button data-testid="suggestion-item-button" onClick={onClick} className="autocomplete-list-item__button" type="submit" onKeyDown={handleKeyDown} ref={buttonRef}>{t5}</button>;
    $[16] = handleKeyDown;
    $[17] = onClick;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== liClassName || $[21] !== other || $[22] !== t6) {
    t7 = <li className={liClassName} data-testid="suggestion-item" {...other}>{t6}</li>;
    $[20] = liClassName;
    $[21] = other;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}